import { defineStore } from 'pinia'
import { reactive } from 'vue';
import { ref, computed } from 'vue'
import { MenuItem, WebConfig } from './types';

export const useGeneralStore = defineStore('general', () => {
    const theme = ref<string>(localStorage.getItem('theme') || 'light')
    const isDarkTheme = computed(() => theme.value === 'dark');
    const webConfig = reactive<WebConfig>({
        bio: "",
        homePage: null,
        appName: null,
        baseUrl: null,
        tenantName: null,
        description: null,
        language: null,
        type: null,
        address: null,
        email: null,
        phone: null,
        logoUrl: null,
        faviconUrl: null,
        contactPerson: null,
        contactEmail: null,
        contactPhone: null,
        contactAddress: null,
        hasMembership: false,
        memberCanRegister: false,
        memberCanLogin: false,
        socialMenu: [],
        footerMenu: [],
        headerMenu: [],
        supportMenu: [],
        companyMenu: [],
        ecosystemMenu: [],
    });

    const activateTheme = () => {
        // Remove both possible classes to ensure a clean state
        document.documentElement.classList.remove('light', 'dark')
        document.documentElement.classList.add(theme.value)
    }

    const setTheme = (themeValue: string) => {
        if (theme.value) {
            document.documentElement.classList.remove(theme.value as string);
        }

        theme.value = themeValue;
        localStorage.setItem('theme', themeValue);
        activateTheme();
    }

    const loadWebConfig = async () => {
        try {
            var res = await window.$ajaxGet("/public/web-config");
            console.dev("loadWebConfig.res", res);

            if (res.data) {
                if (res.data.webConfig) {
                    webConfig.bio = res.data.webConfig.bio;
                    webConfig.homePage = res.data.webConfig.homePage;
                    webConfig.appName = res.data.webConfig.appName;

                    webConfig.baseUrl = res.data.webConfig.baseUrl;
                    webConfig.tenantName = res.data.webConfig.tenantName;
                    webConfig.description = res.data.webConfig.description;
                    webConfig.language = res.data.webConfig.language;
                    webConfig.type = res.data.webConfig.type;
                    webConfig.email = res.data.webConfig.email;
                    webConfig.phone = res.data.webConfig.phone;
                    webConfig.logoUrl = res.data.webConfig.logoUrl;
                    webConfig.faviconUrl = res.data.webConfig.faviconUrl;
                    webConfig.contactPerson = res.data.webConfig.contactPerson;
                    webConfig.contactEmail = res.data.webConfig.contactEmail;
                    webConfig.contactPhone = res.data.webConfig.contactPhone;
                    webConfig.contactAddress = res.data.webConfig.contactAddress;
                    webConfig.hasMembership = res.data.webConfig.hasMembership;
                    webConfig.memberCanRegister = res.data.webConfig.memberCanRegister;
                    webConfig.memberCanLogin = res.data.webConfig.memberCanLogin;
                }

                if (res.data.menus && res.data.menus.length > 0) {
                    res.data.menus.forEach((menu: MenuItem) => {
                        if (menu.linkPosition === "social") {
                            webConfig.socialMenu.push(menu);
                        } else if (menu.linkPosition === "footer") {
                            webConfig.footerMenu.push(menu);
                        } else if (menu.linkPosition === "header") {
                            webConfig.headerMenu.push(menu);
                        } else if (menu.linkPosition === "support") {
                            webConfig.supportMenu.push(menu);
                        } else if (menu.linkPosition === "company") {
                            webConfig.companyMenu.push(menu);
                        } else if (menu.linkPosition === "ecosystem") {
                            webConfig.ecosystemMenu.push(menu);
                        }
                    });

                    webConfig.headerMenu.sort((a, b) => a.position - b.position);
                    webConfig.supportMenu.sort((a, b) => a.position - b.position);
                    webConfig.companyMenu.sort((a, b) => a.position - b.position);
                    webConfig.footerMenu.sort((a, b) => a.position - b.position);
                    webConfig.socialMenu.sort((a, b) => a.position - b.position);
                    webConfig.ecosystemMenu.sort((a, b) => a.position - b.position);
                }
            }
        } catch (error) {
            console.dev('Error loading web config:', error);
        }
    }

    const restoreSession = async () => {
        try {
            const savedTheme = localStorage.getItem('theme');

            if (!window.isEmpty(savedTheme)) {
                theme.value = savedTheme ?? 'light';
            }

            if(window.isEmpty(savedTheme)) {
                if(document.documentElement.classList.contains("dark")) {
                    theme.value = 'dark';
                } else if(document.documentElement.classList.contains("light")) {
                    theme.value = 'light';
                }

                setTheme(theme.value);
            } else {
                activateTheme();
            }
        } catch (error) {
            console.dev('Error restoring general store session:', error);
        }

        await window.$refreshAccessToken(window.$moduleName);
        await loadWebConfig();
    }

    const toggleTheme = () => {
        if (theme.value === 'dark') {
            setTheme('light')
        } else {
            setTheme('dark')
        }
    }

    return {
        theme,
        isDarkTheme,
        webConfig,
        setTheme,
        toggleTheme,
        restoreSession,
        loadWebConfig,
    }
})
